import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ActivityLogPanel from 'src/components/molecules/ActivityLogPanel'
import CalculationTabTemplate from 'src/components/templates/CalculationTab'
import { ToastTriggerContext } from 'src/context/toast.context'
import { ActivityLogEntityType } from 'src/domain/activityLog'
import { isActivityLogEnabled } from 'src/domain/org'
import { useActivityLogPanel } from 'src/hooks/useActivityLogPanel'
import { useFilteredCalculationDSLs } from 'src/hooks/useFilteredCalculationData'
import Path, { TenantPathParams } from 'src/routes/path'
import {
  getCalculationDSLs,
  PathCalculationMethodsParams,
  selectCalculationDSLsRequestStateByParams,
  selectDenormalizedCalculationDSLsByParams,
  updateCalculationMethods,
} from 'src/slices/calculationDSLs/calculationDSLsSlice'
import { postTenantsIdCalculationDsl } from 'src/slices/editCalculations/editCalculationsSlice'
import {
  ActivityLogScopeTypeEnum,
  CalculationMethodPatchRequest,
  CalculationRoundingTypeEnum,
} from 'src/slices/services/api'
import { useAppDispatch, useAppSelector } from 'src/store'
import Presenter, {
  calculationRoundingTypeOptions,
  precisionOptions,
} from './presenter'

const CalculationTab: React.FC = (): ReactElement => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const toastContext = useContext(ToastTriggerContext)

  const params = useParams<TenantPathParams>() as TenantPathParams

  const role = useAppSelector((state) => state.auth.currentAuthorization?.role)
  // 三井不動産（orgCode: 007）では非表示項目をフィルタリング（表示用）
  const dsls = useFilteredCalculationDSLs(params)
  // API送信用に全DSLを取得（フィルタリングなし）
  const allDsls = useAppSelector(
    selectDenormalizedCalculationDSLsByParams(params)
  )

  const { status, lastUpdatedAt, calculationMethod } = useAppSelector(
    selectCalculationDSLsRequestStateByParams(params)
  )
  const editCalculationsStatus = useAppSelector(
    (state) => state.forms.editCalculations.status
  )

  const {
    isOpen: isLogPanelOpen,
    open: openLogPanel,
    close: closeLogPanel,
    logs: activityLogs,
    isLoading: isActivityLogsLoading,
    isLoadingMore: isActivityLogsLoadingMore,
    hasMore: hasMoreActivityLogs,
    dateRangeFilter: activityLogDateRangeFilter,
    errorMessage: activityLogError,
    onLoadMore: handleLoadMoreActivityLogs,
  } = useActivityLogPanel({
    orgCode: params.orgCode,
    entityType: ActivityLogEntityType.Calculation,
    scopeType: ActivityLogScopeTypeEnum.Tenant,
    scopeId: calculationMethod?.tenantId,
  })

  // API Request
  useEffect(() => {
    if (status === 'idle') {
      dispatch(getCalculationDSLs(params))
    }
  }, [status, params, dispatch])

  const handleEditCalculationMethod = async (
    roundingTypeText: CalculationRoundingTypeEnum,
    precision: number
  ) => {
    if (!calculationMethod) {
      return
    }

    const calculationMethods: CalculationMethodPatchRequest[] = [
      {
        id: calculationMethod?.id,
        tenantId: calculationMethod?.tenantId,
        roundingType: roundingTypeText,
        precision,
      },
    ]
    const updateCalculationMethodsParam: PathCalculationMethodsParams = {
      orgCode: params.orgCode,
      storeCode: params.storeCode,
      tenantCode: params.tenantCode,
      calculationMethods,
    }

    try {
      await dispatch(
        updateCalculationMethods(updateCalculationMethodsParam)
      ).unwrap()

      toastContext.sendToast({
        variant: 'success',
        title: '端数処理の更新をしました。',
      })
    } catch (error) {
      toastContext.sendToast({
        variant: 'error',
        title: '端数処理の更新に失敗しました。',
      })
    }
  }

  const tableData = useMemo(() => {
    return Presenter.convertToTableData(dsls ?? [])
  }, [dsls])

  const handleClickButton = () => {
    navigate(
      Path.startEditCalculation(
        params.orgCode,
        params.storeCode,
        params.tenantCode
      )
    )
  }

  const handleResetCalculationDSL = async () => {
    // APIには全項目（非表示項目含む）を送信する必要があるためallDslsを使用
    if (!calculationMethod?.tenantId || !allDsls || allDsls.length === 0) {
      toastContext.sendToast({
        variant: 'error',
        title: 'テナント情報または算出項目が取得できませんでした',
      })
      return
    }

    try {
      // 各算出項目のDSLをリセット（FixedDSLがある場合はその値を、ない場合は"0"を設定）
      // 非表示項目も含めて全項目をAPIに送信
      const resetCalculations = allDsls.map((dsl) => ({
        calculationId: dsl.calculationItem.id,
        dsl: dsl.calculationItem.fixedDsl || '0',
        ocrFormats: [],
      }))

      await dispatch(
        postTenantsIdCalculationDsl({
          tenantId: calculationMethod.tenantId,
          editCalculations: resetCalculations,
        })
      ).unwrap()

      toastContext.sendToast({
        variant: 'success',
        title: '算出ロジックをリセットしました',
      })

      dispatch(getCalculationDSLs(params))
    } catch (error) {
      toastContext.sendToast({
        variant: 'error',
        title: 'リセット処理に失敗しました',
      })
    }
  }

  const handleOpenLogPanel = useCallback(() => {
    if (!calculationMethod) {
      toastContext.sendToast({
        variant: 'error',
        title: '履歴の取得に失敗しました: テナント情報が取得できませんでした',
      })
      return
    }
    openLogPanel()
  }, [calculationMethod, toastContext, openLogPanel])

  return (
    <>
      <CalculationTabTemplate
        onClickEditButton={handleClickButton}
        onClickEditCalculationMethod={handleEditCalculationMethod}
        onClickResetButton={
          editCalculationsStatus === 'loading'
            ? () => {}
            : handleResetCalculationDSL
        }
        isLoading={status === 'loading'}
        columns={tableData.columns}
        records={tableData.records}
        role={role}
        calculationMethod={calculationMethod}
        roundingTypeOptions={calculationRoundingTypeOptions}
        precisionOptions={precisionOptions}
        lastUpdatedAt={lastUpdatedAt}
        onClickHistoryButton={
          isActivityLogEnabled(params.orgCode) ? handleOpenLogPanel : undefined
        }
      />
      <ActivityLogPanel
        orgCode={params.orgCode}
        open={isLogPanelOpen}
        title="確定履歴"
        logs={activityLogs}
        isLoading={isActivityLogsLoading}
        isLoadingMore={isActivityLogsLoadingMore}
        hasMore={hasMoreActivityLogs}
        showDiff={false}
        dateRangeFilter={activityLogDateRangeFilter}
        errorMessage={activityLogError}
        onClose={closeLogPanel}
        onLoadMore={handleLoadMoreActivityLogs}
      />
    </>
  )
}

export default CalculationTab
